import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { stats, testimonials } from '@/mocks/moguvideo';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蘑菇视频 - 官方正版视频平台',
    description: '蘑菇视频致力于为用户提供海量高清正版视频内容，打造安全、流畅、极致的观影体验。了解蘑菇视频的品牌故事、发展历程和核心优势。',
    url: 'https://lygshjx.com.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇视频',
      url: 'https://lygshjx.com.cn/',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && typeof value === 'string') {
          params.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9tbf1f7mpqf8k8iif00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
        setTimeout(() => setFormSuccess(false), 5000);
      } else {
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="蘑菇视频致力于为用户提供海量高清正版视频内容，打造安全、流畅、极致的观影体验。了解蘑菇视频的品牌故事、发展历程和核心优势。"
        keywords="蘑菇视频品牌,蘑菇视频介绍,蘑菇视频联系方式,蘑菇视频官网"
        canonicalUrl="https://lygshjx.com.cn/about"
        ogType="website"
        schemaData={pageSchema}
        pageType="webpage"
      />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '关于我们' },
          ]}
        />

        <main className="w-full">
          {/* Brand Intro */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                    关于蘑菇视频
                  </h1>
                  <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                    蘑菇视频是领先的在线视频平台，致力于为用户提供海量高清正版视频内容，
                    打造安全、流畅、极致的观影体验。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                      品牌故事
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      蘑菇视频成立于2020年，团队由一群热爱视频内容的互联网人组成。我们深知用户对高质量视频内容的需求，也看到了市场上盗版泛滥、用户体验差的痛点。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      因此，我们创立了蘑菇视频——一个坚持<strong>正版内容</strong>、<strong>极致体验</strong>、<strong>用户至上</strong>的视频平台。经过多年的发展，蘑菇视频已经积累了超过5000万用户和10万+视频内容库，成为用户信赖的视频平台品牌。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      我们的使命是：<strong>让每个人都能安全、便捷地享受高质量视频内容。</strong>
                    </p>
                  </div>
                  <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20modern%20minimalist%20tech%20office%20with%20coral%20pink%20and%20warm%20golden%20accents%2C%20creative%20team%20working%20environment%2C%20large%20monitors%20displaying%20video%20content%2C%20natural%20light%2C%20warm%20atmosphere%2C%20professional%20photography%2C%20clean%20composition%2C%20cinematic%20quality&width=800&height=600&seq=moguvideo-about-brand&orientation=landscape"
                      alt="蘑菇视频品牌故事"
                      title="蘑菇视频品牌故事 - 官方正版视频平台"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full bg-background-100 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    核心价值观
                  </h2>
                  <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                    蘑菇视频始终坚持的核心理念
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    { icon: 'ri-shield-star-line', title: '正版内容', desc: '坚持与国内外数百家内容方合作，全站正版授权，为用户提供安全可靠的内容。' },
                    { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，不断优化产品功能和交互设计，让观影更加便捷舒适。' },
                    { icon: 'ri-rocket-2-line', title: '技术创新', desc: '持续投入技术研发，AI推荐引擎、智能缓冲、4K HDR等技术持续领先行业。' },
                  ].map((item, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-6 md:p-7 text-center">
                      <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-4">
                        <i className={`${item.icon} text-xl text-primary-600`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    用户口碑
                  </h2>
                  <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                    听听蘑菇视频用户怎么说
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {testimonials.map((t, i) => (
                    <div key={i} className="bg-background-50 rounded-xl p-6 md:p-7">
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                          <div className="text-xs text-foreground-500">{t.role}</div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        "{t.content}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    联系我们
                  </h2>
                  <p className="text-foreground-500 max-w-xl mx-auto text-sm md:text-base">
                    如有任何问题或合作意向，欢迎通过以下方式联系我们
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                        <i className="ri-mail-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-900 text-sm">邮箱</div>
                        <div className="text-sm text-foreground-500">support@lygshjx.com.cn</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                        <i className="ri-phone-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-900 text-sm">电话</div>
                        <div className="text-sm text-foreground-500">400-888-0000</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                        <i className="ri-map-pin-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-900 text-sm">地址</div>
                        <div className="text-sm text-foreground-500">北京市朝阳区蘑菇大厦 12层</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                        <i className="ri-time-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <div className="font-medium text-foreground-900 text-sm">工作时间</div>
                        <div className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</div>
                      </div>
                    </div>
                  </div>

                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-xl p-6 space-y-4"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="visually-hidden"
                    />
                    {formError && (
                      <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                        {formError}
                      </div>
                    )}
                    {formSuccess && (
                      <div className="text-sm text-green-700 bg-green-50 px-4 py-2.5 rounded-lg">
                        感谢您的留言，我们会尽快回复！
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? '提交中...' : '发送留言'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
}